import React, { useEffect, useMemo } from 'react';
import { Calendar, Clock, MapPin, ChevronRight, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import { GoogleEvent } from '../types/calendar.types';
import { format, startOfDay, endOfDay } from 'date-fns';
import { it } from 'date-fns/locale';
import { useGoogleCalendarStore } from '../store/useGoogleCalendarStore';

// Eventi di oggi dal Google Calendar (sola lettura). La create/modifica
// avviene su Google; qui solo visualizzazione.
export const DailyAppointments: React.FC = () => {
  const fetchEvents = useGoogleCalendarStore((s) => s.fetchEvents);
  const events = useGoogleCalendarStore((s) => s.events);
  const configured = useGoogleCalendarStore((s) => s.configured);
  const loading = useGoogleCalendarStore((s) => s.loading);

  useEffect(() => {
    const load = () => {
      const now = new Date();
      fetchEvents(startOfDay(now), endOfDay(now));
    };
    load();
    // Aggiorna ogni 5 minuti per restare allineati.
    const interval = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchEvents]);

  // Eventi di oggi derivati dallo store (ordinati per orario).
  const todayEvents = useMemo<GoogleEvent[]>(() => {
    const now = new Date();
    const dayStart = startOfDay(now);
    const dayEnd = endOfDay(now);
    return events
      .filter((ev) => {
        const d = new Date(ev.startDate);
        return d >= dayStart && d <= dayEnd;
      })
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  }, [events]);

  const getNextEvent = () => {
    const now = new Date();
    return todayEvents.find((ev) => new Date(ev.startDate) > now);
  };

  const nextEvent = getNextEvent();

  if (loading && todayEvents.length === 0) {
    return (
      <Card className="mb-6">
        <CardContent className="p-4 md:p-6">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4" />
            <div className="space-y-3">
              <div className="h-3 bg-gray-200 rounded" />
              <div className="h-3 bg-gray-200 rounded w-5/6" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-4 md:mb-6">
      <CardHeader className="p-4 md:p-6 pb-3 md:pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="w-5 h-5 text-yellow-600" />
            Impegni di Oggi - {format(new Date(), 'dd MMMM yyyy', { locale: it })}
          </CardTitle>
          <Link to="/calendar">
            <Button variant="outline" size="sm" className="text-yellow-600 border-yellow-600 hover:bg-yellow-50">
              <ChevronRight className="w-4 h-4 mr-1" />
              Vedi tutto
            </Button>
          </Link>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {!configured ? (
          <div className="text-center py-6">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Google Calendar non ancora collegato</p>
          </div>
        ) : todayEvents.length === 0 ? (
          <div className="text-center py-6">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Nessun impegno programmato per oggi</p>
            <p className="text-gray-400 text-xs mt-1">Goditi una giornata tranquilla!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Prossimo impegno */}
            {nextEvent && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-800">Prossimo impegno</span>
                </div>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h4 className="font-semibold text-gray-900 break-words">{nextEvent.title}</h4>
                    <p className="text-sm text-gray-600 break-words">
                      {nextEvent.allDay ? 'Tutto il giorno' : format(new Date(nextEvent.startDate), 'HH:mm', { locale: it })}
                      {nextEvent.location && ` • ${nextEvent.location}`}
                    </p>
                  </div>
                  {nextEvent.allDay && (
                    <Badge className="text-xs bg-purple-100 text-purple-800 border-purple-200">Giornata</Badge>
                  )}
                </div>
              </div>
            )}

            {/* Tutti gli eventi di oggi */}
            <div className="space-y-3">
              {todayEvents.map((ev) => (
                <div key={ev.id} className="flex items-center justify-between gap-2 p-2 sm:p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="p-2 flex-shrink-0 rounded-lg bg-blue-100 text-blue-800">
                      <Calendar className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm text-gray-900 truncate">{ev.title}</h4>
                      <div className="flex items-center gap-x-4 gap-y-1 text-xs text-gray-500 flex-wrap min-w-0">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {ev.allDay ? 'Tutto il giorno' : format(new Date(ev.startDate), 'HH:mm', { locale: it })}
                        </div>
                        {ev.location && (
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            <span className="truncate">{ev.location}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {ev.htmlLink && (
                    <a href={ev.htmlLink} target="_blank" rel="noreferrer" title="Apri su Google Calendar" className="flex-shrink-0 ml-3">
                      <Button variant="ghost" size="sm" className="p-1 h-6 w-6 hover:bg-blue-50">
                        <ExternalLink className="w-3 h-3 text-blue-600" />
                      </Button>
                    </a>
                  )}
                </div>
              ))}
            </div>

            {/* Riepilogo */}
            <div className="pt-3 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>Totale impegni: {todayEvents.length}</span>
                <span className="text-xs text-gray-400">Fonte: Google Calendar</span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
